import { CartData } from '../../../Cart/types'
import { CheckoutCart, Contact, SummaryLineItem } from '../types'
import { ShippingOption } from './summary'
import { ChangeResolveDetails, ClickResolveDetails, LineItem } from '@stripe/stripe-js'
import { CheckoutDataAddress, StripeAddress } from './types'

function hasRecurringPrice(cart: CartData): boolean {
  return cart.items.some(({ price_id }) => {
    const price = globalThis.globalResourceData.pricesById[price_id]
    return price.is_recurring
  })
}

export function hasPhysicalProductsWithParams(cart: CartData): boolean {
  return cart.items.some(({ product_id, variant_id }) => {
    const product = globalThis.Checkout.productsById[product_id]
    const variant = globalThis.Checkout.variantsById[variant_id]
    let productType = variant?.product_type
    productType = productType || product?.product_type
    return productType == 'physical'
  })
}

export function hasUpgradeDowngradeItems(cart: CheckoutCart): boolean {
  return cart.items.some((item) => (item.type as string) === 'upgradeDowngrade')
}

export function canCalculateSubtotalFromCart(cart: CartData): boolean {
  return !hasRecurringPrice(cart)
}

export function getStripeMode(cart: CartData, amount: number): 'subscription' | 'payment' | 'setup' {
  if (amount == 0) return 'setup'

  return hasRecurringPrice(cart) ? 'subscription' : 'payment'
}

export function getCartEstimatedTotal(cart: CartData): number {
  const estimatedAmount = cart.items.reduce((acc, { price_id }) => {
    const price = globalThis.globalResourceData.pricesById[price_id]
    return acc + price.initial_price_cents
  }, 0)
  return estimatedAmount
}

export function convertToStripeAddress(address: CheckoutDataAddress): StripeAddress {
  return {
    line1: address.address_one,
    line2: address.address_two,
    city: address.city,
    state: address.state,
    postal_code: address.postal_code,
    country: address.country,
  }
}

export function convertAddressFromStripe(address: StripeAddress): CheckoutDataAddress {
  return {
    address_one: address.line1,
    address_two: address.line2,
    city: address.city,
    state: address.state,
    country: address.country,
    postal_code: address.postal_code,
  }
}

// NOTE: not sure why we have this second way of address, but it's mostly
// likely it's due to how we implemented for Rebilly respecting its form
export function convertAddressFromSession(address: {
  address?: string
  address_2?: string | null
  city: string
  state: string
  zip: string
  country: string
}): CheckoutDataAddress {
  return {
    address_one: address.address,
    address_two: address.address_2,
    city: address.city,
    state: address.state,
    country: address.country,
    postal_code: address.zip,
  }
}

// Stripe requires the wallet line items to add up to no more than the amount the Elements are
// set to, and throws `IntegrationError: The amount N is less than the total amount of the line
// items provided` otherwise — which, inside a wallet event handler, means the sheet is never
// resolved and Apple/Google time it out.
//
// So the tax line is derived from what the product/shipping/discount lines leave unaccounted
// for rather than from the server's `tax_amount_cents`. That amount only sits outside the line
// amounts when the merchant's Stripe Tax behavior is exclusive; with inclusive tax (the EU
// default) the VAT is already inside them and adding it again overshoots the total by the VAT.
export function calculateStripeLineItems(
  lineItems: SummaryLineItem[],
  totalAmountCents: number,
  shippingOption?: ShippingOption,
  discountAmount?: number
): LineItem[] | undefined {
  // Both are missing until the first order preview returns, and a wallet can be clicked before
  // then. `lineItems` is optional to Stripe, so the sheet shows the total on its own rather than
  // a TypeError that would leave it unresolved.
  if (!lineItems?.length || totalAmountCents == null) return undefined

  const allLineItems = lineItems.map(({ priceCents, description }) => {
    return {
      name: description,
      amount: priceCents,
    }
  })
  if (shippingOption) {
    allLineItems.push({
      name: shippingOption.description,
      amount: shippingOption.amount_cents,
    })
  }
  if (discountAmount) {
    allLineItems.push({
      name: 'Discount',
      amount: -discountAmount,
    })
  }

  const remainder = totalAmountCents - allLineItems.reduce((sum, { amount }) => sum + amount, 0)
  // Anything the server has added on top of the lines we know about — exclusive tax, plus any
  // rounding drift.
  if (remainder > 0) allLineItems.push({ name: 'Taxes', amount: remainder })
  // The lines claim more than the buyer is being charged, so there is no itemization that both
  // reconciles and stays truthful. Omitting them leaves the sheet showing only the total.
  if (remainder < 0) return undefined

  return allLineItems
}

export const STRIPE_SKIP_SHIPPING_ID = 'skip it'
export function calculateStripeChangeResolveDetails(
  lineItems: SummaryLineItem[],
  quotesResponse: ShippingOption[],
  shippingOption: ShippingOption,
  totalAmountCents: number,
  discountAmount: number
): ChangeResolveDetails {
  const stripeLineItems = calculateStripeLineItems(lineItems, totalAmountCents, shippingOption, discountAmount)
  return {
    ...(stripeLineItems ? { lineItems: stripeLineItems } : {}),
    shippingRates: quotesResponse?.map((option) => {
      const { description, amount_cents } = option
      return {
        id: JSON.stringify(option),
        amount: amount_cents,
        displayName: description,
      }
    }) ?? [
      {
        id: STRIPE_SKIP_SHIPPING_ID,
        amount: 0,
        displayName: 'Free',
      },
    ],
  }
}

// What the wallet sheet is asked to collect and show when the express button is clicked.
// `needsAddress` is the caller's decision; this only holds the two shapes it produces.
export function calculateStripeClickResolveDetails({
  needsAddress,
  phoneNumberRequired,
  lineItems,
  totalAmountCents,
  shippingOption,
  discountAmountCents,
}: {
  needsAddress: boolean
  phoneNumberRequired: boolean
  lineItems: SummaryLineItem[]
  totalAmountCents: number
  shippingOption?: ShippingOption
  discountAmountCents?: number
}): ClickResolveDetails {
  const stripeLineItems = calculateStripeLineItems(lineItems, totalAmountCents, shippingOption, discountAmountCents)
  return {
    emailRequired: true,
    phoneNumberRequired,
    shippingAddressRequired: needsAddress,
    ...(needsAddress
      ? // Stripe only accepts shipping rates alongside a shipping address, and the real ones are
        // not known until the address round trip answers.
        { shippingRates: [{ id: 'calculating', amount: 0, displayName: 'Calculating...' }] }
      : // A field on the wallet sheet rather than a step of its own. Without the address round
        // trip it is the only address the order will get.
        { billingAddressRequired: true }),
    ...(stripeLineItems ? { lineItems: stripeLineItems } : {}),
  }
}

// The name sent with the confirmation token. Always a string: Stripe rejects a token with no name
// while the Payment Element hides its name field. The server names the contact with it when the
// token has no shipping address, so a part the buyer never typed must not arrive as "undefined"
// (ENG-8030). Without a typed first name the wallet's name stands in: the server takes the first
// word as the first name, so a lone last name would land there.
export function confirmationTokenBillingName(
  contact: Pick<Contact, 'first_name' | 'last_name'>,
  walletName?: string
): string {
  const typedName = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
  return (contact.first_name ? typedName : walletName || typedName) || ''
}

export function parseCssVars(element: HTMLElement, obj: Record<string, string>): any {
  Object.keys(obj ?? {}).forEach((key) => {
    const value = obj[key]
    if (typeof value === 'object') {
      obj[key] = parseCssVars(element, value)
    } else {
      if (typeof value === 'string' && value.includes('--')) {
        obj[key] = getComputedStyle(element).getPropertyValue(value)
      }
    }
  })
  return obj
}

export function garlicRetrieve(key: string): string {
  return globalThis.cfGarlicUtils?.retrieve(key) ?? ''
}

export function garlicStore(key: string, value: string): void {
  globalThis.cfGarlicUtils?.store(key, value)
}

// A state can omit an element whose 'ready' fires, e.g. `.stripe-contact-form` when only the link input renders.
export function observeStripeElement(
  observer: Pick<ResizeObserver, 'observe'>,
  stripeElement: HTMLElement | null,
  loaderElement: Element | null
): void {
  if (!stripeElement) {
    loaderElement?.classList.add('forceHide')
    return
  }
  observer.observe(stripeElement)
}

export function showInputError(errorfield: HTMLElement | null, message: string): void {
  if (!errorfield) return
  errorfield.style.height = '20px'
  errorfield.previousElementSibling.classList.add('elInputError')
  errorfield.textContent = message
}

export function clearInputError(errorfield: HTMLElement | null): void {
  if (!errorfield) return
  errorfield.style.height = '0px'
  errorfield.previousElementSibling.classList.remove('elInputError')
  errorfield.textContent = ''
}
